import { useEffect } from "react";
import { FileText, Handshake, UserCheck, CreditCard, Ban, ShieldX, Mail, Scale } from "lucide-react";
import { motion } from "framer-motion";

export default function Terms() {
  // Fix: Ensures the page starts at the header on navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <div className="bg-[#fdfcf7] min-h-screen pb-20 font-sans">
      {/* 
          HERO / HEADER SECTION 
      */}
      <section className="py-20 px-6 border-b-4 border-foreground bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="inline-block px-3 py-1 bg-primary text-white font-black uppercase text-xs tracking-widest mb-6 border-2 border-foreground shadow-brutal-sm">
              Service Agreement
            </div>
            <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-4">
              Terms of <span className="text-primary italic">Service.</span>
            </h1>
            <p className="font-bold text-muted-foreground flex items-center gap-2">
              <Scale className="w-5 h-5 text-primary" /> Last modified: {lastUpdated}
            </p>
          </motion.div>
        </div>
      </section>

      {/* 
          LEGAL CONTENT 
      */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="bg-white border-4 border-foreground p-8 md:p-12 shadow-brutal space-y-16">
          
          {/* Introduction */}
          <div className="prose prose-slate max-w-none">
            <p className="text-xl font-bold leading-relaxed text-foreground">
              By accessing and utilizing the DmFlio automation engine, you enter into a binding legal agreement 
              to comply with the following Terms of Service. These terms govern your professional use of our 
              infrastructure and official Meta API integrations.
            </p>
          </div>

          <div className="grid gap-12">
            {/* Section 1 */}
            <TermSection 
              icon={<Handshake className="w-8 h-8" />}
              title="1. Scope of Service"
              content={
                <p className="font-bold text-muted-foreground leading-relaxed">
                  DmFlio provides cloud-based social media automation. Users agree to utilize this service strictly 
                  for lawful professional purposes and in total compliance with Meta (Instagram) Platform 
                  Policies. Any attempt to bypass platform limitations through our service is strictly prohibited.
                </p>
              }
            />

            {/* Section 2 */}
            <TermSection 
              icon={<UserCheck className="w-8 h-8" />}
              title="2. Account Responsibility"
              content={
                <p className="font-bold text-muted-foreground leading-relaxed">
                  As the primary account holder, you maintain full responsibility for all activities conducted 
                  through your DmFlio credentials. You are obligated to maintain the confidentiality of your 
                  access tokens and secure your integration endpoints to prevent unauthorized automation.
                </p>
              }
            />

            {/* Section 3 */}
            <TermSection 
              icon={<CreditCard className="w-8 h-8" />}
              title="3. Billing & Commercial Terms"
              content={
                <div className="space-y-4 font-bold text-muted-foreground">
                  <p>Subscriptions are billed on a recurring monthly cycle. DmFlio provides the software 
                     infrastructure "as is" and does not offer guarantees regarding specific business 
                     revenue, follower growth, or engagement metrics.</p>
                  <p className="text-foreground border-l-4 border-primary pl-4 uppercase text-xs tracking-wider">
                    Users are responsible for ensuring sufficient quota for their automation rules.
                  </p>
                </div>
              }
            />

            {/* Section 4 */}
            <TermSection 
              icon={<Ban className="w-8 h-8 text-primary" />}
              title="4. Prohibited Activity"
              content={
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 font-black uppercase text-xs tracking-tight">
                  <li className="bg-primary/5 p-4 border-2 border-foreground">Spam or Abusive Bulk Messaging</li>
                  <li className="bg-primary/5 p-4 border-2 border-foreground">Violation of Instagram Community Rules</li>
                  <li className="bg-primary/5 p-4 border-2 border-foreground">Automated Harassment of Users</li>
                  <li className="bg-primary/5 p-4 border-2 border-foreground">Redistribution of API Data</li>
                </ul>
              }
            />

            {/* Section 5 */}
            <TermSection 
              icon={<ShieldX className="w-8 h-8" />}
              title="5. Termination Protocols"
              content={
                <p className="font-bold text-muted-foreground leading-relaxed">
                  DmFlio reserves the right to suspend or terminate service access immediately and without 
                  notice if a user is found to be in violation of these terms or Meta Platform Policies. 
                  Suspended accounts are not eligible for billing credits or refunds.
                </p>
              }
            />

            {/* Section 6 */}
            <TermSection 
              icon={<Mail className="w-8 h-8" />}
              title="6. Legal Support"
              content={
                <div className="bg-foreground text-white p-8 border-2 border-foreground shadow-brutal-sm flex flex-col md:flex-row justify-between items-center gap-6">
                  <div className="space-y-1 text-center md:text-left">
                    <p className="font-black uppercase tracking-widest text-xs text-primary">Compliance & Legal:</p>
                    <p className="text-2xl font-bold">hello@dmflio.com</p>
                  </div>
                  <div className="px-6 py-2 border-2 border-white/20 font-black uppercase text-[10px] tracking-widest">
                    DmFlio Technologies
                  </div>
                </div>
              }
            />
          </div>
        </div>
      </section>
    </div>
  );
}

// Sub-component for individual terms blocks
function TermSection({ icon, title, content }) {
  return (
    <div className="flex flex-col md:flex-row gap-6 md:gap-10 border-t-2 border-muted pt-10">
      <div className="w-16 h-16 bg-white border-2 border-foreground flex items-center justify-center shrink-0 shadow-brutal-sm">
        {icon}
      </div>
      <div className="space-y-4 flex-1">
        <h2 className="text-3xl font-black uppercase tracking-tighter italic leading-none">{title}</h2>
        <div className="text-lg">
          {content}
        </div>
      </div>
    </div>
  );
}